// Remembers where to return after signing in (or signing up), for flows that start while signed
// out — today only the AI-app consent page (/oauth/consent?req=…), which an AI app opens in the
// browser.
//
// Only that exact path shape is accepted, both when storing and when consuming, so a crafted link
// can never turn the login page into an open redirect. Entries expire with the consent request
// itself (10 minutes on the API), so an abandoned flow can't hijack a much later login.

const KEY = 'postLoginRedirect';
const ALLOWED = /^\/oauth\/consent\?req=[A-Za-z0-9_-]{16,128}$/;
export const POST_LOGIN_REDIRECT_TTL_MS = 10 * 60 * 1000;

interface Stored {
    path: string;
    at: number;
}

export function rememberPostLoginRedirect(path: string): void {
    if (!ALLOWED.test(path)) return;
    try {
        const entry: Stored = { path, at: Date.now() };
        sessionStorage.setItem(KEY, JSON.stringify(entry));
    } catch {
        // storage unavailable (private mode) — the user can retry from their AI app
    }
}

function read(): Stored | null {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return null;
        const v = JSON.parse(raw) as Partial<Stored>;
        if (typeof v.path !== 'string' || typeof v.at !== 'number') return null;
        return { path: v.path, at: v.at };
    } catch {
        return null;
    }
}

function valid(v: Stored | null): v is Stored {
    return !!v && ALLOWED.test(v.path) && Date.now() - v.at < POST_LOGIN_REDIRECT_TTL_MS;
}

/** Returns the stored path (once) if it is still an allowed, unexpired target, else null. */
export function consumePostLoginRedirect(): string | null {
    const v = read();
    clearPostLoginRedirect();
    return valid(v) ? v.path : null;
}

/** Remembers the page the browser is on, if it's an allowed target. Called wherever the app sends
 *  a signed-out user to /login (PrivateRoute, the API client's 401 handler, the consent page). */
export function rememberCurrentLocation(): void {
    rememberPostLoginRedirect(window.location.pathname + window.location.search);
}

/** Where to go right after signing in or up: a pending consent page first, else onboarding for a
 *  user who hasn't finished it, else the dashboard. Onboarding skipped for a consent flow still
 *  runs on the user's next visit — PrivateRoute sends unfinished users to /profile-setup. */
export function postLoginDestination(needsOnboarding: boolean): string {
    return consumePostLoginRedirect() ?? (needsOnboarding ? '/profile-setup' : '/dashboard');
}

export function clearPostLoginRedirect(): void {
    try {
        sessionStorage.removeItem(KEY);
    } catch {
        // ignore
    }
}
